import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { I18nService } from '@app/core/i18n/i18n.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { Angulartics2GoogleAnalytics, Angulartics2GoogleTagManager } from 'angulartics2';
import { NgcCookieConsentService, NgcStatusChangeEvent } from 'ngx-cookieconsent';
import { supportedLanguages } from 'src/translations/languages';
import { AuthSessionService } from '../auth-session/auth-session.service';
import { CrispChatService } from '../crisp-chat/crisp-chat.service';
import { DeviceDetectService } from '../device-detect/device-detect.service';
import { LocalStorageService } from '../localstorage/localstorage.service';
import { TrackingEvent } from './tracking-event.interface';
declare const cordova: any;
declare const FirebasePlugin: any;
declare const gtag: any;
declare const window: any;

/**
 * Service to handle cookie and tracking service.
 */
@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  private angulartics2GoogleTagManager: Angulartics2GoogleTagManager = inject(Angulartics2GoogleTagManager);
  private angulartics2GoogleAnalytics: Angulartics2GoogleAnalytics = inject(Angulartics2GoogleAnalytics);
  private router: Router = inject(Router);
  private deviceDetectService: DeviceDetectService = inject(DeviceDetectService);
  private ccService: NgcCookieConsentService = inject(NgcCookieConsentService);
  private crispChatService: CrispChatService = inject(CrispChatService);
  private localStorageService: LocalStorageService = inject(LocalStorageService);
  private translateService: TranslateService = inject(TranslateService);
  private i18nService: I18nService = inject(I18nService);
  private authSesssionService: AuthSessionService = inject(AuthSessionService);
  private hasCookieAccepted = false;
  private isNativeDevice = false;
  private initializedRouterTracking = false;

  constructor() {
    this.isNativeDevice = this.deviceDetectService.isCordova;
  }

  /**
   * Initialize the iOS IDFA process and handle.it
   */
  initIDFA(): void {
    const idfaPlugin = cordova.plugins.idfa;

    idfaPlugin
      .getInfo()
      .then((info: any) => {
        if (!info.trackingLimited) {
          return info.idfa || info.aaid;
        } else if (info.trackingPermission === idfaPlugin.TRACKING_PERMISSION_NOT_DETERMINED) {
          return idfaPlugin.requestPermission().then((result: any) => {
            if (result === idfaPlugin.TRACKING_PERMISSION_AUTHORIZED) {
              return idfaPlugin.getInfo().then((infoResult: any) => infoResult.idfa || infoResult.aaid);
            }
            return false;
          });
        }
      })
      .then((idfaOrAaid: any) => {
        this.initNativeTracking(idfaOrAaid);
      });
  }

  /**
   * Initialize the cookie conset process, compliant with GDPR.
   */
  initCookie() {
    if (this.deviceDetectService.isIOS) {
      this.ccService.destroy();
      return;
    }

    this.initWebEnvironment();

    const supportedLanguag = Object.keys(supportedLanguages).join('|');

    if (this.localStorageService.getItem('cookieAccepted') === 'true') {
      this.checkTagManager();
    } else {
      this.initGA();
      this.ccService.statusChange$.subscribe((event: NgcStatusChangeEvent) => {
        // you can use this.ccService.getConfig() to do stuff...
        // cookieconsent_status
        if (event.status === 'allow') {
          this.localStorageService.setItem('cookieAccepted', 'true');
          this.initGTM();
          this.ccService.destroy();
        }
      });
      const browserLang: any = this.translateService.getBrowserLang()?.toLowerCase();
      // Provide all supported languages
      if (this.deviceDetectService.isCordova) {
        this.translateService.use(browserLang.match('/' + supportedLanguag + '/') ? browserLang : 'en');
      }
      this.translateCookieBar();
    }
  }

  /**
   * Initialize the translation observable of the cookiebar consent.
   */
  initTranslationCookieBar() {
    this.i18nService.changedSelectedLanguage$.subscribe(() => {
      if (this.localStorageService.getItem('cookieAccepted') !== 'true') {
        this.translateCookieBar();
      }
    });
  }

  /**
   * Track a event and send to  Analytics.
   *
   * @param eventToTrack
   *   Event to track
   */
  trackEvent(eventToTrack: TrackingEvent) {
    if (this.isNativeDevice) {
      FirebasePlugin.logEvent(eventToTrack.event, eventToTrack);
      return;
    }
    this.hasCookieAccepted
      ? this.angulartics2GoogleTagManager.eventTrack(eventToTrack.event, eventToTrack)
      : this.angulartics2GoogleAnalytics.eventTrack(eventToTrack.event, eventToTrack);
  }

  /**
   * Init Google Tag Manager.
   */
  private initGTM() {
    if (this.isNativeDevice) {
      this.initNativeTracking(true);
      return;
    }
    const bodyScript = document.createElement('noscript');
    const headerScript = document.createElement('script');

    gtag('consent', 'default', {
      ad_storage: 'granted',
      analytics_storage: 'granted',
      personalization_storage: 'granted',
      functionality_storage: 'granted',
      security_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
    });

    document.body.appendChild(bodyScript);
    document.head.appendChild(headerScript);
    // start angulartics tracking

    this.hasCookieAccepted = true;
    this.angulartics2GoogleTagManager.startTracking();
    this.initRoutingTracking();
    this.initHotJar();
    this.authSesssionService.currentUserProfile$.subscribe((user) => {
      // Required by Brevo, must use the email address
      if (user) {
        localStorage.setItem('authMail', user.mail);
      } else {
        localStorage.removeItem('authMail');
      }
      this.angulartics2GoogleTagManager.setUsername(user?.uid.toString() ?? '');
      gtag('config', environment.googleAnalyticId, {
        user_id: user?.uid.toString(),
      });
    });

    if (!this.deviceDetectService.isCordova) {
      this.crispChatService.initChatBot();
    }
  }

  /**
   * Init Google Analytics.
   */
  private initGA() {
    if (this.isNativeDevice) {
      this.initNativeTracking(false);
      return;
    }

    gtag('consent', 'default', {
      ad_storage: 'denied',
      analytics_storage: 'denied',
      personalization_storage: 'denied',
      functionality_storage: 'granted',
      security_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    gtag('config', '${environment.googleAnalyticId}', { anonymize_ip: true, cookie_update: false });
    this.angulartics2GoogleAnalytics.startTracking();
    this.initRoutingTracking();
  }

  /**
   * Check if the user has accepted the cookie and init the tracking.
   */
  private checkTagManager() {
    // TODO: something wrong with the status returned type, the actual value is a string.
    !!this.ccService.open()?.hasConsented() ? this.initGTM() : this.initGA();
    this.ccService.destroy();
  }

  /**
   * Translate the cookie bar.
   */
  private translateCookieBar() {
    if (this.deviceDetectService.isIOS) {
      return;
    }

    this.ccService.getConfig().content = this.ccService.getConfig().content || {};
    const content: any = this.ccService.getConfig().content;
    // Override default messages with the translated ones
    content.message = this.translateService.instant('content.message');
    content.allow = this.translateService.instant('content.allow');
    content.deny = this.translateService.instant('content.deny');
    content.link = this.translateService.instant('content.link');
    content.href = this.translateService.instant('content.href');
    content.policy = this.translateService.instant('content.policy');

    try {
      this.ccService.destroy(); // remove previous cookie bar (with default messages)
    } catch (err) {
      console.error('Error on destroy cookie bar', err);
    }
    this.ccService.init(this.ccService.getConfig()); // update config with translated messages
  }

  /**
   * Initialize the native mobile device tracking with Firebase.
   *
   * @param {boolean} consent
   *   Tell if the user accepted or not the consent of tracking.
   */
  private initNativeTracking(consent = false) {
    const consents: { [key: string]: any } = {};
    const consentValue = consent
      ? FirebasePlugin.AnalyticsConsentStatus.GRANTED
      : FirebasePlugin.AnalyticsConsentStatus.DENIED;
    consents[FirebasePlugin.AnalyticsConsentMode.ANALYTICS_STORAGE] = consentValue;
    consents[FirebasePlugin.AnalyticsConsentMode.AD_STORAGE] = consentValue;
    consents[FirebasePlugin.AnalyticsConsentMode.AD_USER_DATA] = consentValue;
    consents[FirebasePlugin.AnalyticsConsentMode.AD_PERSONALIZATION] = consentValue;

    FirebasePlugin.setAnalyticsConsentMode(
      consents,
      () => {
        FirebasePlugin.setAnalyticsCollectionEnabled(true);
      },
      (error: string) => {
        console.error('Error setting consent mode: ' + error);
      }
    );
    if (consent) {
      this.authSesssionService.currentUserProfile$.subscribe((user) => {
        FirebasePlugin.setUserId(user?.uid.toString() ?? '0');
      });
    }
  }

  /**
   * Init HotJar service.
   */
  private initHotJar(): void {
    const headerScript = document.createElement('script');
    headerScript.innerHTML = `(function(h,o,t,j,a,r){ h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)}; h._hjSettings={hjid:2949317,hjsv:6}; a=o.getElementsByTagName('head')[0]; r=o.createElement('script');r.async=1; r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv; a.appendChild(r); })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`;
    document.head.appendChild(headerScript);
  }

  /**
   *  Initialize the tracking of Angular Routing.
   */
  private initRoutingTracking() {
    if (this.initializedRouterTracking) {
      return;
    }
    this.initializedRouterTracking = true;
    this.router.events.subscribe((item) => {
      if (item instanceof NavigationEnd) {
        if (this.isNativeDevice) {
          FirebasePlugin.logEvent('page_view', { page: item.url });
          return;
        }
        this.hasCookieAccepted
          ? this.angulartics2GoogleTagManager.pageTrack(item.url)
          : this.angulartics2GoogleAnalytics.pageTrack(item.url);
      }
    });
  }

  /**
   * Init the web tracking environment.
   */
  private initWebEnvironment() {
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    window.gtag = () => window.dataLayer.push(arguments);
    gtag('js', new Date());
    const headerScript = document.createElement('script');
    const bodyScript = document.createElement('noscript');

    bodyScript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=
    ${environment.googleTagManagerId}"
    height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

    headerScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}      
      gtag('js', new Date());
      (function (w, d, s, l, i) {
        w[l] = w[l] || []; w[l].push({
          'gtm.start':
            new Date().getTime(), event: 'gtm.js'
        }); var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s), dl = l !== 'dataLayer' ? '&l=' + l : ''; j.async = true; j.src =
            'https://www.googletagmanager.com/gtm.js?id=' + i + dl; f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${environment.googleTagManagerId}');`;

    document.body.appendChild(bodyScript);
    document.head.appendChild(headerScript);
  }
}
